// Workspace Skill section — configure primary Skill for an API and publish
// through the existing Skill Market pipeline (create → zip upload → submit).
// Skill lifecycle is independent of API revision state; drift is soft.
//
// Authoring is structured: name/description/slug/version are plain inputs,
// metadata.xapi.* (categories/tags/permissions/examples/dependencies) lives in
// a collapsible "Advanced configuration" section with real form controls, and
// the markdown editor is reserved for the skill's actual instructional body.
// assembleSkillMarkdown() combines all of it into the final SKILL.md at publish
// time — there's no hand-written YAML frontmatter anywhere in this UI.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { XPrim } from '../../primitives.jsx';
import { XShell } from '../../shell.jsx';
import { XApi } from '../../services.js';
import { MarkdownEditor } from '../../components/MarkdownEditor.jsx';
import { TagInput } from '../../components/TagInput.jsx';
import { CollapsibleSection } from '../../components/CollapsibleSection.jsx';
import {
  assembleSkillMarkdown,
  buildScaffoldBody,
  bumpPatchVersion,
  skillMdToZipFile,
} from '../../skills/scaffold-from-api.js';

const SLUG_RE = /^(?=.{3,64}$)[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SEMVER_RE = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:[-+][0-9A-Za-z.-]+)?$/;

const endpointKeyColon = (e) => e.endpointKey || `${e.method}:${e.path}`;
const endpointKeyDash = (e) =>
  e.endpointKey || `${String(e.method || 'get').toLowerCase()}-${String(e.path || '').replace(/^\//, '')}`;

export const SkillSection = ({ api, onApiRefreshed }) => {
  const { toast } = XPrim.useToast();
  const { navigate } = XShell.useRoute();

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  // Authoring form
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [version, setVersion] = useState('0.1.0');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  // Advanced config (metadata.xapi.*)
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [permissions, setPermissions] = useState({
    externalWrites: false,
    spendsCredits: false,
    personalData: false,
  });
  const [examples, setExamples] = useState([]);
  // Body-only editor content (no frontmatter)
  const [bodyMarkdown, setBodyMarkdown] = useState('');

  const load = useCallback(async () => {
    if (!api?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await XApi.apiSvc.skillContext(api.id);
      setCtx(data);
      const eps = data.endpoints || [];
      const allKeys = eps.map(endpointKeyColon);
      setSlug(data.linkedSkill?.slug || data.suggestedSlug || '');
      setName(data.linkedSkill?.name || data.name || '');
      setDescription(data.linkedSkill?.description || data.description || '');
      const latestVer = data.linkedSkill?.versions?.[0]?.version;
      setVersion(latestVer ? bumpPatchVersion(latestVer) : '0.1.0');

      const prefill = data.skillPrefill;
      if (prefill) {
        setCategories(prefill.categories || []);
        setTags(prefill.tags || []);
        setPermissions({
          externalWrites: !!prefill.permissions?.externalWrites,
          spendsCredits: !!prefill.permissions?.spendsCredits,
          personalData: !!prefill.permissions?.personalData,
        });
        setExamples(prefill.examples || []);
        setBodyMarkdown((prevBody) => prevBody || prefill.bodyMarkdown || '');

        // Reverse-map previously-submitted dependencies back to selectedKeys.
        // Deps store `endpoint` as either the real endpointKey or a derived
        // slug (dash-form) when the endpoint had none — match either shape
        // against each candidate endpoint so selection survives round-trips
        // regardless of which form was used when it was last assembled.
        const depEndpoints = new Set(
          (prefill.dependencies || []).map((d) => d.endpoint).filter(Boolean),
        );
        const matched = eps
          .filter(
            (e) => depEndpoints.has(endpointKeyDash(e)) || depEndpoints.has(endpointKeyColon(e)),
          )
          .map(endpointKeyColon);
        setSelectedKeys(new Set(matched.length ? matched : allKeys));
      } else {
        setCategories([]);
        setTags([]);
        setPermissions({ externalWrites: false, spendsCredits: false, personalData: false });
        setExamples([]);
        setSelectedKeys(new Set(allKeys));
        // Only seed when still empty so a manual "Refresh status" click doesn't
        // clobber in-progress edits.
        setBodyMarkdown(
          (prevBody) =>
            prevBody ||
            buildScaffoldBody({
              apiName: data.name,
              description: data.description,
              hostPrefix: data.host,
              gatewayUrl: XApi.helpers.buildGatewayUrl(data.host, data.integrationMode),
              endpoints: eps,
            }),
        );
      }
    } catch (e) {
      setError(e?.message || 'Failed to load skill context');
    } finally {
      setLoading(false);
    }
  }, [api?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const endpoints = ctx?.endpoints || [];
  const linked = ctx?.linkedSkill;
  const drift = !!ctx?.drift;

  const selectedEndpoints = useMemo(
    () => endpoints.filter((e) => selectedKeys.has(endpointKeyColon(e))),
    [endpoints, selectedKeys],
  );

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const addExample = () =>
    setExamples((prev) => {
      if (prev.length >= 20) return prev;
      return [...prev, { title: '', prompt: '' }];
    });
  const updateExample = (idx, field, value) =>
    setExamples((prev) => prev.map((ex, i) => (i === idx ? { ...ex, [field]: value } : ex)));
  const removeExample = (idx) => setExamples((prev) => prev.filter((_, i) => i !== idx));

  const bindSkillId = async (skillId) => {
    await XApi.apiSvc.updateBasic(api.id, { linkedSkillId: skillId });
    const fresh = await XApi.apiSvc.get(api.id);
    onApiRefreshed?.(fresh);
    await load();
  };

  const unbind = async () => {
    if (!confirm('Unlink primary skill from this API? The skill itself stays in the market.')) {
      return;
    }
    setBusy('unbind');
    setError('');
    try {
      await XApi.apiSvc.updateBasic(api.id, { linkedSkillId: '' });
      toast({ title: 'Skill unlinked', type: 'success' });
      const fresh = await XApi.apiSvc.get(api.id);
      onApiRefreshed?.(fresh);
      await load();
    } catch (e) {
      setError(e?.message || 'Unlink failed');
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    const s = slug.trim().toLowerCase();
    if (!SLUG_RE.test(s)) {
      setError('Slug must be 3–64 chars: lowercase letters, numbers, hyphens');
      return;
    }
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!SEMVER_RE.test(version.trim())) {
      setError('Version must be semver, e.g. 0.1.0');
      return;
    }
    if (!bodyMarkdown.trim()) {
      setError('Skill body content cannot be empty');
      return;
    }
    if (!ctx?.host) {
      setError('API needs a host prefix before publishing a skill (set it in 基本信息)');
      return;
    }

    setBusy('publish');
    setError('');
    try {
      const md = assembleSkillMarkdown({
        name: name.trim(),
        description: description.trim(),
        hostPrefix: ctx.host,
        endpoints: selectedEndpoints,
        categories,
        tags,
        permissions,
        examples,
        bodyMarkdown,
      });

      const availability = await XApi.skillService.slugAvailability(s);
      // available OR owned by me
      const mine = await XApi.skillService.mine();
      const owned = (mine || []).find((item) => item.slug === s);
      if (!availability?.available && !owned) {
        setError(
          availability?.reason ||
            availability?.message ||
            `Slug "${s}" is not available`,
        );
        return;
      }

      let skill = owned;
      if (!skill) {
        skill = await XApi.skillService.create({
          slug: s,
          name: name.trim(),
          description: description.trim() || undefined,
          categories: categories.length ? categories : (ctx.category ? [ctx.category] : undefined),
        });
      }

      // Bind as primary skill before submit so review resolve + UI stay consistent.
      if (ctx.linkedSkillId !== skill.id) {
        await bindSkillId(skill.id);
      }

      const zip = await skillMdToZipFile(md, `${s}-${version.trim()}.zip`);
      const ver = await XApi.skillService.uploadZip(s, zip, {
        version: version.trim(),
        name: name.trim(),
        description: description.trim(),
      });

      await XApi.skillService.submit(
        s,
        ver.id,
        'Submitted from API workspace',
        ver.sourceType || 'upload',
      );

      await XApi.apiSvc.updateSkillFingerprint(api.id, {
        host: ctx.currentFingerprint?.host || ctx.host,
        major: ctx.currentFingerprint?.major || ctx.currentMajor,
        endpointKeys:
          ctx.currentFingerprint?.endpointKeys ||
          selectedEndpoints.map(
            (e) => e.endpointKey || `${e.method}:${e.path}`,
          ),
        skillVersionId: ver.id,
      });

      toast({
        title: 'Submitted to Skill Market review',
        type: 'success',
      });
      await load();
    } catch (e) {
      setError(e?.message || e?.data?.message || 'Publish failed');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 32, color: 'var(--fg-3)' }}>Loading skill context…</div>
    );
  }

  if (!ctx) {
    return (
      <div style={{ padding: 32, color: 'var(--fg-3)' }}>
        {error || 'Skill context unavailable'}
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 32px 48px', maxWidth: 920 }}>
      <div className="row between" style={{ marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Skill</h2>
          <p style={{ margin: '6px 0 0', color: 'var(--fg-3)', fontSize: 13, lineHeight: 1.5, maxWidth: 560 }}>
            Configure one primary Agent Skill for this API, then publish through the Skill Market review flow.
            Skill 上架 ≠ API 上架 — they use separate pipelines.
          </p>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => navigate('skills-console')}>
          Open Skill Console
        </button>
      </div>

      {drift && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 14px',
            borderRadius: 8,
            background: 'rgba(234,179,8,0.12)',
            border: '1px solid rgba(234,179,8,0.35)',
            color: '#b45309',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <strong>Drift detected.</strong> Serving endpoints/host changed since the last skill package sync.
          Review selected endpoints, bump version, and re-submit.
        </div>
      )}

      {linked && (
        <div
          className="card"
          style={{
            padding: 16,
            marginBottom: 16,
            border: '1px solid var(--line)',
            borderRadius: 10,
            background: 'var(--bg-1)',
          }}
        >
          <div className="row between" style={{ gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div className="micro">Linked primary skill</div>
              <div style={{ fontWeight: 600, marginTop: 4 }}>
                {linked.name}{' '}
                <span className="mono" style={{ color: 'var(--fg-3)', fontWeight: 400 }}>
                  {linked.slug}
                </span>
              </div>
              <div className="row gap-2" style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-3)' }}>
                <span className="tag">{linked.status}</span>
                {linked.versions?.[0]?.version && (
                  <span className="mono">latest pkg v{linked.versions[0].version}</span>
                )}
                {linked.submissions?.[0] && (
                  <span>submission: {linked.submissions[0].status}</span>
                )}
              </div>
            </div>
            <div className="row gap-2">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => navigate('skill-detail', { slug: linked.slug })}
              >
                Market page
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy != null}
                onClick={unbind}
              >
                Unlink
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12, color: 'var(--danger, #dc2626)', fontSize: 13 }}>
          {error}
        </div>
      )}

      <div className="col gap-3">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
          }}
          className="skill-meta-grid"
        >
          <label className="col gap-1" style={{ fontSize: 12 }}>
            <span className="micro">Slug</span>
            <input
              className="input mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
              disabled={!!linked}
              placeholder="my-api"
            />
          </label>
          <label className="col gap-1" style={{ fontSize: 12 }}>
            <span className="micro">Version (semver)</span>
            <input
              className="input mono"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="0.1.0"
            />
          </label>
          <label className="col gap-1" style={{ fontSize: 12 }}>
            <span className="micro">Display name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="col gap-1" style={{ fontSize: 12 }}>
            <span className="micro">Short description</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
        </div>

        <div>
          <div className="micro" style={{ marginBottom: 8 }}>
            Dependencies (endpoints this skill needs)
          </div>
          {!endpoints.length ? (
            <div style={{ color: 'var(--fg-4)', fontSize: 13 }}>
              No visible endpoints on the active version. Add endpoints under 管理 API first.
            </div>
          ) : (
            <div
              style={{
                border: '1px solid var(--line)',
                borderRadius: 8,
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              {endpoints.map((ep) => {
                const key = endpointKeyColon(ep);
                const checked = selectedKeys.has(key);
                return (
                  <label
                    key={ep.id || key}
                    className="row gap-2"
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--line)',
                      fontSize: 12,
                      cursor: 'pointer',
                      alignItems: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleKey(key)}
                    />
                    <span className="mono" style={{ color: 'var(--fg-2)' }}>
                      {ep.method}
                    </span>
                    <span className="mono" style={{ flex: 1 }}>
                      {ep.path}
                    </span>
                    <span style={{ color: 'var(--fg-3)' }}>{ep.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <CollapsibleSection title="Advanced configuration">
          <div className="col gap-4">
            <div className="col gap-1">
              <span className="micro">Categories</span>
              <TagInput
                value={categories}
                onChange={setCategories}
                placeholder="Add a category…"
                maxItems={8}
                maxItemLength={40}
              />
            </div>
            <div className="col gap-1">
              <span className="micro">Tags</span>
              <TagInput
                value={tags}
                onChange={setTags}
                placeholder="Add a tag…"
                maxItems={20}
                maxItemLength={40}
              />
            </div>
            <div className="col gap-2">
              <span className="micro">Permissions</span>
              {[
                { key: 'externalWrites', label: 'Writes external state outside xapi' },
                { key: 'spendsCredits', label: "May spend the user's credits/balance" },
                { key: 'personalData', label: 'Handles personal/user data' },
              ].map((p) => (
                <label key={p.key} className="row gap-2" style={{ alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={!!permissions[p.key]}
                    onChange={(e) =>
                      setPermissions((prev) => ({ ...prev, [p.key]: e.target.checked }))
                    }
                  />
                  {p.label}
                </label>
              ))}
            </div>
            <div className="col gap-2">
              <div className="row between" style={{ alignItems: 'center' }}>
                <span className="micro">Examples</span>
                <button type="button" className="btn btn-sm" onClick={addExample} disabled={examples.length >= 20}>
                  Add example
                </button>
              </div>
              {examples.map((ex, idx) => (
                <div
                  key={idx}
                  className="col gap-2"
                  style={{ padding: 10, border: '1px solid var(--line)', borderRadius: 8 }}
                >
                  <input
                    className="input"
                    placeholder="Title"
                    value={ex.title}
                    maxLength={120}
                    onChange={(e) => updateExample(idx, 'title', e.target.value)}
                  />
                  <textarea
                    className="input"
                    placeholder="Prompt"
                    rows={2}
                    value={ex.prompt}
                    maxLength={2000}
                    onChange={(e) => updateExample(idx, 'prompt', e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-sm"
                    style={{ alignSelf: 'flex-end' }}
                    onClick={() => removeExample(idx)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleSection>

        <div>
          <div className="micro" style={{ marginBottom: 8 }}>
            Skill instructions (Markdown)
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--fg-4)', lineHeight: 1.5 }}>
            Just the skill's core content — name, description, and metadata above are combined
            automatically into SKILL.md at publish time.
          </p>
          <MarkdownEditor
            value={bodyMarkdown}
            onChange={setBodyMarkdown}
            height={380}
            placeholder="# My skill…"
            maxLength={200_000}
          />
        </div>

        <div className="row gap-2" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy != null}
            onClick={publish}
          >
            {busy === 'publish'
              ? 'Submitting…'
              : linked
                ? 'Submit new skill version'
                : 'Create & submit to Skill Market'}
          </button>
          <button type="button" className="btn" disabled={busy != null} onClick={load}>
            Refresh status
          </button>
        </div>

        <p style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-4)', lineHeight: 1.5 }}>
          Host for dependency resolve: <span className="mono">{ctx.host || '—'}</span>
          {' · '}
          Major: <span className="mono">v{ctx.currentMajor}</span>
          {' · '}
          After admin approval the skill appears on the market and on this API&apos;s Skill tab.
        </p>
      </div>
    </div>
  );
};

export default SkillSection;
